import { CircularProgress, FormHelperText, Typography } from '@mui/material';
import classnames from 'classnames';
import SelectedDatabaseInputs from 'js/components/data_sources/add_ds_form/SelectedDatabaseInputs/SelectedDatabaseInputs';
import HeaderMetaConfiguration from 'js/components/destination/HeaderMetaConfiguration/HeaderMetaConfiguration';
import { CONNECTION_CATEGORY_NOSQL } from 'js/constants/connectionCategories';
import { FORM_TYPE_EDIT_DESTINATION } from 'js/constants/formTypes';
import { SUPPORT_KEY_SINK_FORM_NOSQL } from 'js/constants/supportPageKeys';
import JSONUtils from 'js/lib/JSONUtils';
import { getEmptyRequiredFields } from 'js/lib/ValidationUtils';
import { cloneDeep, filter, forEach, get, has, isEmpty, map } from 'lodash';
import { ReactComponent as RefreshIcon } from 'new/components/icons/fi-rr-refresh.svg';
import { Box } from 'new/elements/Box';
import { EmptyView } from 'new/elements/EmptyView';
import { NavScroller } from 'new/elements/NavScroller';
import { NavScrollerContext } from 'new/elements/NavScroller/NavScroller';
import { Stack } from 'new/elements/Stack';
import { tryCatchAsync } from 'new/utils/TryCatch';
import { UiMode } from 'new/constants/uiMode';
import React from 'react';
import { ResourcePopoverMenu } from 'src/new/elements/ResourcePopoverMenu';
import Constants from '../../../../Constants';
import DebouncedText from '../../../elements/editors/mui/DebouncedText';
import FormatCardContainer from '../FormatCardContainer';
import DatabaseList from './DatabaseList';
import styles from './NoSQLConfigurator.module.scss';

import 'js/components/data_sources/add_ds_form/SelectBucket.scss';
import './DatabaseConfigurator.scss';

const initialState = {
  databasesIds: [],
  databases: {},
  collections: {},
  selectedDatabaseId: null,
  expandedCollectionId: null,
  collectionsForDatabase: {},
  showPreview: false,
  isNewCollectionInDatabaseList: false,
};

const ObjectStyles = {
  tableNameField: {
    wrapperDiv: {
      display: 'flex',
      flexDirection: 'column',
      marginBottom: 16,
      marginTop: 8,
    },
  },
};

class NoSQLConfigurator extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      databasesIds: [],
      collectionsIds: [],
      databases: {},
      collections: {},
      selectedDatabaseId: null,
      expandedCollectionId: null,
      collectionsForDatabase: {},
      configFields: [],
      showPreview: false,
      isNewCollectionInDatabaseList: false,
    };
  }

  componentDidMount() {
    const configFields = this.getAdvanceConfigFields();
    if (this.props.selectedCredentialId) {
      //Fetch probe tree for this data source
      this.setState(
        {
          ...initialState,
          configFields,
        },
        () => {
          this.onRequestTree();
        },
      );
    } else {
      this.setState({
        ...initialState,
        configFields,
      });
    }

    // this.props.loadDBDataTypes(this.props.selectedConnection);

    if (this.props.setContextualSupport) {
      this.props.setContextualSupport(SUPPORT_KEY_SINK_FORM_NOSQL);
    }
  }

  componentDidUpdate(prevProps) {
    let {
      selectedCredentialId,
      selectedDatabase,
      selectedCollection,
      sinkConfig,
      probeData,
      probeDataFlat,
    } = this.props;

    if (this.props.isNewCollection && !prevProps.isNewCollection) {
      // with collection field
      let configFields = this.getAdvanceConfigFields();
      this.setState({
        configFields,
      });
    }
    if (prevProps.selectedCredentialId !== selectedCredentialId) {
      this.props.onRequestProbeTree();
    }

    if (probeData && probeData !== prevProps.probeData) {
      let databasesIds = [],
        databases = {},
        collections = {},
        collectionsForDatabase = {},
        collectionNamesByDatabase = {};

      if (get(probeData, '[0].type') === 'database') {
        forEach(probeData, (d) => {
          let database = {
            id: d.id,
            name: d.name,
          };
          databasesIds.push(d.id);
          databases[d.id] = database;
          collectionsForDatabase[d.id] = [];
          collectionNamesByDatabase[d.id] = [];
          forEach(d.children, (c) => {
            let collection = {
              id: c.id,
              name: c.name,
              database: d.name,
            };
            collections[c.id] = collection;
            collectionsForDatabase[d.id].push(c.id);
            collectionNamesByDatabase[d.id].push(c.name);
          });
        });

        let updatingSelectedCollection = false;
        if (has(sinkConfig, 'collection')) {
          const cId = sinkConfig.database + '/' + sinkConfig.collection;

          if (has(sinkConfig, 'collection') && !!sinkConfig.collection && has(collections, cId)) {
            updatingSelectedCollection = true;
            this.props.updateFormData(prevProps.formType, {
              selectedCollection: cloneDeep(collections[cId]),
            });
          }
        }

        this.setState(
          {
            databasesIds,
            databases,
            collections,
            collectionsForDatabase,
            selectedDatabaseId: selectedDatabase || this.state.selectedDatabaseId,
          },
          () => {
            const nodePath = sinkConfig.database + '/' + sinkConfig.collection;

            if (
              has(this.props, 'sinkConfig') &&
              (has(sinkConfig, 'collection') || has(sinkConfig, 'name')) &&
              probeDataFlat[nodePath] &&
              probeDataFlat[nodePath].scanned &&
              (!selectedCollection || (selectedCollection && updatingSelectedCollection))
            ) {
              this.openCollectionEditor(nodePath, sinkConfig.mapping);
            }

            // required for some form validations
            this.props.updateFormData(prevProps.formType, {
              collectionNamesByDatabase,
            });
          },
        );
      } else {
        this.setState({
          ...initialState,
        });
      }
    }

    // TODO: do we need this case
    // if (
    //   !isEmpty(this.state.collections) &&
    //   !this.state.selectedDatabaseId &&
    //   !this.state.collections[sinkConfig.database + '/' + sinkConfig.collection]
    // ) {

    //   // in this case, collection will not come in the collections list.
    //   this.openCollectionEditor(
    //     sinkConfig.database + '/' + sinkConfig.collection,
    //     sinkConfig.mapping,
    //     true
    //   );
    // }

    if (selectedDatabase !== prevProps.selectedDatabase) {
      this.setState({ selectedDatabaseId: selectedDatabase });
    }
  }

  componentWillUnmount() {
    if (this.props.unsetContextualSupport) {
      this.props.unsetContextualSupport(SUPPORT_KEY_SINK_FORM_NOSQL);
    }
  }

  getAdvanceConfigFields() {
    let fieldsForThisConnection = this.props.sinkConfigFields
      ? this.props.selectedConnection
        ? this.props.sinkConfigFields[this.props.selectedConnection]
        : this.props.sinkConfigFields[0]
      : null;

    let ignoredFields = [
      'database',
      'mapping',
      'collection',
      'data_format',
      ...Constants.HC_SINK_FIELD_NAMES,
    ];

    return filter(fieldsForThisConnection, (o) => ignoredFields.indexOf(o.name) === -1);
  }

  selectDatabase = (dId) => {
    if (dId === this.state.selectedDatabaseId) {
      this.setState(
        {
          selectedDatabaseId: null,
        },
        () => {
          this.props.onCollectionSelected(null, false);
        },
      );
    } else {
      this.probeNode(dId);
      this.setState({
        selectedDatabaseId: dId,
      });
    }

    this.setState({
      isNewCollectionInDatabaseList: false,
    });
  };

  expandCollection = (cId) => {
    if (cId) {
      let collection = this.state.collections[cId];
      // this.probeNode(cId);
      this.setState(
        {
          expandedCollectionId: cId,
          collectionNotFound: false,
        },
        () => {
          this.props.onCollectionSelected(collection, false);
        },
      );
    }
  };

  openCollectionEditor = (cId, mapping, moveToNextStep = false) => {
    if (cId) {
      let collection = this.state.collections[cId];
      if (collection) {
        // no need to call probe for collection
        // this.probeNode(cId);
        this.setState(
          {
            expandedCollectionId: cId,
            collectionNotFound: false,
          },
          () => {
            this.props.onCollectionSelected(collection, false);
          },
        );
      } else if (this.props.isEditMode) {
        let { sinkConfig } = this.props;
        collection = {
          database: sinkConfig.database,
          id: cId,
          name: sinkConfig.collection,
        };

        // this.probeNode(cId);
        this.setState(
          {
            expandedCollectionId: cId,
            collectionNotFound: true,
          },
          () => {
            this.props.onCollectionSelected(collection, true);
            /**
             * New collection created and data is pending to get inserted,
             * In edit case move to the last screen
             */
            if (moveToNextStep) {
              this.props.nextFormStage();
            }
          },
        );
      }
    } else {
      this.setState({ collectionNotFound: false }, () => {
        this.props.onCollectionSelected(
          {
            database: this.state.selectedDatabaseId,
            name: '',
            id: this.state.selectedDatabaseId + '/New Collection',
          },
          true,
        );
      });
    }

    this.setState({
      isNewCollectionInDatabaseList: false,
    });
  };

  editSelectedCollection = (key, value, isNewCollection) => {
    let st = cloneDeep(this.props.selectedCollection);
    st[key] = value;
    if (key === 'name') {
      st.id = st.database + '/' + value;
    }

    this.props.onCollectionSelected(st, isNewCollection);
  };

  probeNode = (nodeId) => {
    const node = this.props.probeDataFlat?.[nodeId];

    if (node && !node.scanned && !this.props.isLoadingProbeData) {
      this.props.onRequestProbeTree(false, node);
    }
  };

  onSinkConfigChanged = (key, value) => {
    const { sinkConfig } = this.props;

    this.props.onSinkConfigChanged({
      ...sinkConfig,
      [key]: value,
    });
  };

  handleShowPreview = (state = true) => {
    this.setState({ showPreview: state });
  };

  doesNewCollectionExist = (newCollection) => {
    const collections =
      this.state.collectionsForDatabase[this.state.selectedDatabaseId]?.map(
        (collection) => collection.split('/')[1],
      ) || [];

    return collections.some((collection) => collection === newCollection);
  };

  onChange = ({ name, value }) => {
    if (name && name !== '') {
      const { sinkConfig } = this.props;
      const data = { ...sinkConfig, [name]: value };
      this.props.onSinkConfigChanged(data);
    }
  };

  onRequestTree = () => {
    if (
      this.props.sinkConfig &&
      this.props.sinkConfig.database &&
      this.props.sinkConfig.collection
    ) {
      // TODO: need to check this function..
      this.props.onRequestInitialDBProbeTree({
        type: 'collection',
        name: this.props.sinkConfig.collection,
        parent: this.props.sinkConfig.database,
      });
    } else {
      // TODO: need to check this function..
      this.props.onRequestProbeTree();
    }
  };

  onChangeTableInput = (tId) => {
    if (tId.length === 0) {
      this.setState(
        {
          expandedCollectionId: '',
        },
        () => {
          this.props.onCollectionSelected(null);
        },
      );
    }

    if (tId) {
      const collection = this.state.collections?.[tId];
      if (!collection) {
        this.props.onCollectionSelected({
          id: tId,
          name: tId?.split('/')[1],
          database: tId?.split('/')[0],
          scanned: false,
        });
        this.setState({
          expandedCollectionId: tId,
        });
      } else {
        this.probeNode(tId);
        this.setState(
          {
            expandedCollectionId: tId,
          },
          () => {
            this.props.onCollectionSelected(collection);
          },
        );
      }
    }
  };

  render() {
    let {
      databasesIds,
      databases,
      collections,
      selectedDatabaseId,
      expandedCollectionId,
      collectionsForDatabase,
      configFields,
      isNewCollectionInDatabaseList,
    } = this.state;
    let {
      addedDataSink,
      selectedCollection,
      dataSchema,
      probeDataFlat,
      isLoadingProbeData,
      connectionMetas,
      selectedConnection,
      isNewCollection,
      sinkConfig,
      dataSetId,
      selectedBucket,
      orderedDataSchema,
      excludeByAttrName,
      onToggleExclude,
      updateOrderedDataSchema,
      setContextualSupport,
      unsetContextualSupport,
      onSinkConfigChanged,
    } = this.props;
    const meta = connectionMetas[selectedConnection];
    const isLoadingTree =
      (isEmpty(this.props.probeData) && isLoadingProbeData) ||
      // After selecting collection coming back again, then showing loading collections for <Selected Database>
      // instead of showing database doesn't have any collections.
      (!isEmpty(this.props.probeData) && isEmpty(databases) && !isEmpty(selectedCollection));
    const { dataSet: nexset } = this.props;
    const sinkName = sinkConfig?.name ?? addedDataSink?.name ?? '';
    const descValue = sinkConfig?.description ?? addedDataSink?.description ?? '';
    const resourceConfig = this.getResourceConfig();

    return (
      <div className="database-destination-configurator nosql-database-destination-configurator">
        <div className="main-content">
          <HeaderMetaConfiguration
            nexsetId={nexset?.id}
            isDestination
            name={sinkName}
            description={descValue}
            connection={selectedConnection}
            onChange={this.onChange}
            sourceOrDestinationChipChildren={
              <ResourcePopoverMenu
                isDestination
                resourceConfig={resourceConfig}
                connectorType={selectedConnection}
                jsonConfigOnSave={this.onSaveAdvancedMode}
                onSwitchUiMode={this.onSwitchUiMode}
                jsonConfigOnClose={() => {
                  if (this.props.goBack) {
                    this.props.goBack();
                  }
                }}
                disabledActions={['view-json-config']}
              />
            }
            nexsetChipChildren={
              <ResourcePopoverMenu
                isDestination
                nexsetId={nexset?.id}
                resourceConfig={resourceConfig}
                connectorType={selectedConnection}
                jsonConfigOnSave={this.onSaveAdvancedMode}
                onSwitchUiMode={this.onSwitchUiMode}
                jsonConfigOnClose={() => {
                  if (this.props.goBack) {
                    this.props.goBack();
                  }
                }}
                disabledActions={['view-nexset-details']}
              />
            }
          />

          <Stack width="100%" className="configuration" padding={1}>
            <NavScroller
              navOptions={
                Boolean(selectedCollection)
                  ? [
                      { label: 'Destination', id: 'no-sql-destination' },
                      { label: 'Data Format', id: 'no-sql-data-format' },
                    ]
                  : [{ label: 'Destination', id: 'no-sql-destination' }]
              }
            >
              <NavScrollerContext.Consumer>
                {(consumer) => {
                  return (
                    <>
                      <Box
                        id="no-sql-destination"
                        className={classnames({
                          box: true,
                          [styles.box_focused]: consumer?.activeScroll === 'no-sql-destination',
                        })}
                        title={
                          <Typography fontSize={14}>
                            <b>Destination</b>
                            <Typography variant="bodySmall" marginLeft={2}>
                              Select the destination for storing all processed and transformed data
                              files.
                            </Typography>
                          </Typography>
                        }
                      >
                        <Stack flex={1} width={'100%'} minHeight="380px">
                          <div className="locator">
                            <SelectedDatabaseInputs
                              databasePlaceholder="Select a database bellow or type the name here"
                              tablePlaceholder="Select a location bellow or type the name here"
                              selectedDatabaseId={selectedDatabaseId}
                              selectedTableId={selectedCollection?.id}
                              selectDatabase={this.selectDatabase}
                              selectTable={this.onChangeTableInput}
                              databasesIds={databasesIds}
                              rightAction={
                                <div className="refresh-buttons-container">
                                  {this.props.isLoadingProbeData ? (
                                    <div className="refresh-loader-container">
                                      <CircularProgress
                                        size={19}
                                        sx={{
                                          color: 'currentColor',
                                        }}
                                      />
                                    </div>
                                  ) : (
                                    <RefreshIcon
                                      className="refresh-icon"
                                      fontSize="large"
                                      onClick={() => this.props.onRequestProbeTree()}
                                    />
                                  )}
                                </div>
                              }
                            />
                          </div>

                          {this.props.probeError ? (
                            <EmptyView
                              title="There was an error loading resources"
                              actionTitle="Try Again"
                              actionProps={{
                                onClick: this.onRequestTree,
                              }}
                            />
                          ) : isLoadingTree ? (
                            <div className="select-bucket-tree-list-loading">
                              <CircularProgress />
                              Connecting to {meta?.displayName}...
                            </div>
                          ) : (
                            !isEmpty(databases) && (
                              <Stack maxHeight="340px" overflow="auto">
                                <DatabaseList
                                  credentialId={this.props.selectedCredentialId}
                                  databases={map(databasesIds, (i) => databases[i])}
                                  selectedDatabaseId={selectedDatabaseId}
                                  selectDatabase={this.selectDatabase}
                                  tablesForDatabase={collectionsForDatabase}
                                  tables={collections}
                                  openTableEditor={this.openCollectionEditor.bind(this)}
                                  expandedTableId={expandedCollectionId}
                                  expandTable={this.expandCollection.bind(this)}
                                  selectedTable={selectedCollection || {}}
                                  isNewTable={isNewCollection}
                                  probeDataFlat={probeDataFlat}
                                  selectedConnection={selectedConnection}
                                  shouldShowExpandedView={false}
                                  connectionCategory={CONNECTION_CATEGORY_NOSQL}
                                  handleShowPreview={this.handleShowPreview}
                                />
                              </Stack>
                            )
                          )}
                        </Stack>
                      </Box>

                      {selectedCollection ? (
                        <Box
                          title="Format"
                          id="no-sql-data-format"
                          className={classnames({
                            box: true,
                            [styles.box_focused]: consumer?.activeScroll === 'no-sql-data-format',
                          })}
                        >
                          <div className="table-field-container">
                            <div
                              className="table-field-wrapper"
                              style={ObjectStyles.tableNameField.wrapperDiv}
                            >
                              <label htmlFor={'collectionName'}>Collection Name *</label>
                              <DebouncedText
                                id="CollectionName"
                                data-testid="collection-name"
                                placeholder={'Collection name'}
                                value={selectedCollection?.name}
                                debounceTimeMs={200}
                                error={isNewCollectionInDatabaseList}
                                disabled={!isNewCollectionInDatabaseList && !isNewCollection}
                                onChange={(value) => {
                                  const doestInputValueExistInCollection =
                                    this.doesNewCollectionExist(value);

                                  if (doestInputValueExistInCollection) {
                                    this.setState(
                                      {
                                        isNewCollectionInDatabaseList: true,
                                      },
                                      () => {
                                        this.editSelectedCollection('name', value, false);
                                      },
                                    );
                                  } else {
                                    this.setState(
                                      {
                                        isNewCollectionInDatabaseList: false,
                                      },
                                      () => {
                                        this.editSelectedCollection('name', value, true);
                                      },
                                    );
                                  }
                                }}
                              />

                              {isNewCollectionInDatabaseList && (
                                <FormHelperText>
                                  <Typography
                                    variant="bodySmall"
                                    color="error"
                                    style={{ marginLeft: 'var(--spacing-md)' }}
                                  >
                                    Collection name already exists in the database.
                                  </Typography>
                                </FormHelperText>
                              )}
                            </div>

                            {configFields.map((field) => (
                              <div
                                className="table-field-wrapper"
                                style={ObjectStyles.tableNameField.wrapperDiv}
                                key={field.name}
                              >
                                <label htmlFor={field.name}>{field.display_name}</label>
                                <DebouncedText
                                  id={field.name}
                                  value={sinkConfig[field.name] || ''}
                                  debounceTimeMs={200}
                                  onChange={(value) => {
                                    this.onSinkConfigChanged(field.name, value);
                                  }}
                                />
                              </div>
                            ))}

                            {isNewCollection ? (
                              <FormatCardContainer
                                formType={FORM_TYPE_EDIT_DESTINATION}
                                selectedDestinationType={selectedConnection}
                                sinkConfigFields={configFields}
                                addedDataSink={addedDataSink}
                                dataSetId={dataSetId}
                                sinkConfig={sinkConfig}
                                onSinkConfigChanged={onSinkConfigChanged}
                                selectedBucket={selectedBucket}
                                dataSchema={dataSchema}
                                orderedDataSchema={orderedDataSchema}
                                excludeByAttrName={excludeByAttrName}
                                onToggleExclude={onToggleExclude}
                                updateOrderedDataSchema={updateOrderedDataSchema}
                                setContextualSupport={setContextualSupport}
                                unsetContextualSupport={unsetContextualSupport}
                                showHeader={false}
                                connectionCategory={CONNECTION_CATEGORY_NOSQL}
                              />
                            ) : null}
                          </div>
                        </Box>
                      ) : null}
                    </>
                  );
                }}
              </NavScrollerContext.Consumer>
            </NavScroller>
          </Stack>
        </div>
      </div>
    );
  }

  validateRequiredFields = (config) => {
    const { selectedConnection, sinkConfigFields } = this.props;
    const fields = sinkConfigFields[selectedConnection];
    const missingFields = getEmptyRequiredFields(fields, config);
    return !missingFields.length;
  };

  onSaveAdvancedMode = async (sinkConfigString) => {
    if (!JSONUtils.isJSON(sinkConfigString)) {
      return;
    }
    const { nextFormStage, onError = () => {}, onSaveAdvancedMode: saveAdvancedMode } = this.props;

    const parsedConfig = JSON.parse(sinkConfigString);
    const { name, description, sink_config } = parsedConfig;

    const data = {
      ...sink_config,
      name,
      description,
      ['ui.mode']: UiMode.CODE,
    };

    const { error } = await tryCatchAsync(saveAdvancedMode(data));
    if (error) {
      onError(error.message);
      return;
    }

    nextFormStage();
  };

  onSwitchUiMode = () => {
    const { sinkConfig, onSinkConfigChanged } = this.props;
    if (typeof onSinkConfigChanged !== 'function') return;

    onSinkConfigChanged({ ...sinkConfig, ['ui.mode']: UiMode.VIEW });
  };

  getResourceConfig = () => {
    const { sinkConfig, addedDataSink } = this.props;

    const clonedSinkConfig = structuredClone(sinkConfig);

    const name = addedDataSink?.name || clonedSinkConfig.name || '';
    const description = addedDataSink?.description || clonedSinkConfig.description || '';

    delete clonedSinkConfig.name;
    delete clonedSinkConfig.description;

    return {
      name,
      description,
      sink_config: {
        collection: null,
        database: null,
        ...clonedSinkConfig,
      },
    };
  };
}

export default NoSQLConfigurator;
